// Client-facing formatting for the SPA. Mirrors cdk/lambdas/shared/format.ts,
// which the email handlers use. Keep the two in step: the eTOE page and the
// quote email must state the same fee in the same words, because the client
// reads one and signs the other.

export const GST_RATE = 0.1;

const HONORIFICS = new Set([
  'dr', 'dr.', 'doctor',
  'prof', 'prof.', 'professor', 'a/prof', 'a/prof.', 'assoc', 'assoc.',
  'mr', 'mr.', 'mrs', 'mrs.', 'ms', 'ms.', 'miss', 'mx', 'mx.',
  'sir', 'dame', 'rev', 'rev.', 'fr', 'fr.',
]);

/** First name for a greeting, skipping a leading honorific ("Dr Alicia Fenwick" -> "Alicia"). */
export function firstName(fullName?: string | null, fallback = 'there'): string {
  const parts = (fullName ?? '').trim().split(/\s+/).filter(Boolean);
  let i = 0;
  while (i < parts.length - 1 && HONORIFICS.has(parts[i].toLowerCase())) i++;
  return parts[i] || fallback;
}

export function money(n?: number | null): string {
  return typeof n === 'number'
    ? n.toLocaleString('en-AU', { style: 'currency', currency: 'AUD' })
    : 'to be confirmed';
}

export function feeInclGst(exGst?: number | null): number | undefined {
  return typeof exGst === 'number'
    ? Math.round(exGst * (1 + GST_RATE) * 100) / 100
    : undefined;
}

/**
 * "$8,800.00 plus GST ($9,680.00 total)".
 *
 * feeQuotedExGst is EXCLUSIVE of GST and the Xero invoice adds 10% on top, so a
 * proposal that says "incl. GST" understates what the client will be billed.
 */
export function feeText(exGst?: number | null): string {
  if (typeof exGst !== 'number') return 'to be confirmed';
  return `${money(exGst)} plus GST (${money(feeInclGst(exGst))} total)`;
}

/**
 * The four most recent COMPLETED Australian financial years, oldest first.
 *
 * An AFY ends 30 June, so from July onward the current calendar year's FY is
 * complete. Mirrors recentFinancialYears in the Lambda's shared/settings.ts: the
 * default selection is stored as OFFSETS from the newest of these, so both ends have
 * to agree on what the four are.
 */
export function recentFinancialYears(now: Date = new Date()): string[] {
  const endYear = now.getMonth() >= 6 ? now.getFullYear() : now.getFullYear() - 1;
  return [endYear - 3, endYear - 2, endYear - 1, endYear].map((y) => `FY${String(y).slice(-2)}`);
}

/** Offsets from the newest (0 = newest) to the labels they name. */
export function yearsFromOffsets(offsets: number[], now: Date = new Date()): string[] {
  const fys = recentFinancialYears(now);
  return offsets
    .filter((o) => Number.isInteger(o) && o >= 0 && o <= 3)
    .map((o) => fys[fys.length - 1 - o])
    .filter(Boolean);
}
